import {
  CSRF_HEADER,
  type ProjectMemberView,
  type ShareCandidate,
  type ShareConversationResponse,
} from "@esi/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "../api.ts";
import { ApiError, apiError, ok } from "../api-errors.ts";
import { errorCopy } from "../error-copy.ts";
import { formatList } from "../i18n/format.ts";
import { memberName } from "../projects/readers.ts";
import { Link, projectSettingsPath } from "../router.tsx";
import { useSession } from "../session.tsx";
import { AudienceLine, WebResultsNote } from "../trust/AudienceLine.tsx";
import { conversationAudience } from "../trust/audience.ts";
import { useDebounced } from "./ConversationList.tsx";
import { ProjectShareSection } from "./ProjectShareSection.tsx";
import { conversationKeys } from "./queries.ts";

/** Addresses typed one per line, or separated by commas, semicolons or spaces (at most 20). */
export function parseEmails(text: string): string[] {
  return [
    ...new Set(
      text
        .split(/[\s,;]+/)
        .map((e) => e.trim().toLowerCase())
        .filter((e) => e.includes("@")),
    ),
  ].slice(0, 20);
}

export function ShareDialog(props: {
  open: boolean;
  onClose: () => void;
  /** Null for a standalone conversation: recipients are then named by email address. */
  projectId: string | null;
  conversationId: string;
  members: readonly ProjectMemberView[] | undefined;
  /** The viewer may add people to the project (`members.manage`, not frozen). */
  canManageMembers?: boolean;
  /** Some answer of the conversation used web results. */
  webResults?: boolean;
  /** Code ran for some answer of the conversation (agent-turn D20). */
  ranCode?: boolean;
  /**
   * The detail's project share: absent from an API that predates it, which hides the project
   * share (null: not shared).
   */
  projectShare?: { sharedAt: string } | null | undefined;
  /** A frozen project: shares are shown, nothing changes. */
  frozen?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <AppDialog
      open={props.open}
      onClose={props.onClose}
      title={t("share.title")}
      description={props.projectId ? t("share.explain") : t("share.explainStandalone")}
    >
      <ShareForm {...props} />
    </AppDialog>
  );
}

/**
 * Share with project members. Each recipient is checked against the conversation's sources on
 * the server: refused ones come back with a reason that names the person, never a source.
 */
export function ShareForm(props: {
  onClose: () => void;
  projectId: string | null;
  conversationId: string;
  members: readonly ProjectMemberView[] | undefined;
  canManageMembers?: boolean;
  webResults?: boolean;
  ranCode?: boolean;
  projectShare?: { sharedAt: string } | null | undefined;
  frozen?: boolean;
}) {
  const { onClose, projectId, conversationId, members, canManageMembers, webResults } = props;
  const frozen = props.frozen === true;
  // Offered only by an API that knows it (canary: the detail carries the field) and in a project.
  const offersProject = projectId !== null && props.projectShare !== undefined;
  const { t } = useTranslation();
  const id = useId();
  const { csrfToken, userId } = useSession();
  const queryClient = useQueryClient();
  const param = { conversationId };
  const sharesKey = conversationKeys.shares(conversationId);
  const shares = useQuery({
    queryKey: sharesKey,
    queryFn: async () => ok(await api.api.conversations[":conversationId"].shares.$get({ param })),
  });
  const [find, setFind] = useState("");
  const q = useDebounced(find.trim());
  // Who can read the project (#151: direct, group, rule and visibility readers alike).
  const people = useQuery({
    queryKey: conversationKeys.shareCandidates(conversationId, q),
    queryFn: async () =>
      ok(
        await api.api.conversations[":conversationId"]["share-candidates"].$get({
          param,
          query: q ? { q } : {},
        }),
      ),
    enabled: projectId !== null,
    placeholderData: (previous) => previous,
  });
  const [chosen, setChosen] = useState<ReadonlySet<string>>(new Set());
  const [emails, setEmails] = useState("");
  const [outcome, setOutcome] = useState<ShareConversationResponse | null>(null);
  const standalone = projectId === null;
  const names = new Map<string, string | null>(
    shares.data?.map((s) => [s.userId, s.name ?? null]) ?? [],
  );
  // An API before #151 has no candidates endpoint (an unexplained 404, the route missing): the
  // direct members stand in (canary). Any other failure lists nobody rather than a wrong list.
  const before151 =
    people.error instanceof ApiError && people.error.status === 404 && !people.error.explained;
  const pool: readonly ShareCandidate[] | undefined =
    (people.isError ? undefined : people.data?.candidates) ??
    (before151
      ? members?.map((m) => ({
          userId: m.userId,
          displayName: m.displayName ?? memberName(members, m.userId),
          role: m.role,
        }))
      : undefined);
  const candidateNames = new Map<string, string>(pool?.map((c) => [c.userId, c.displayName]) ?? []);
  const nameOf = (id: string) =>
    standalone
      ? (names.get(id) ?? t("share.someone"))
      : (names.get(id) ?? candidateNames.get(id) ?? memberName(members, id));
  const typed = parseEmails(emails);

  const share = useMutation({
    mutationFn: async (to: { userIds: string[] } | { emails: string[] }) =>
      ok(
        await api.api.conversations[":conversationId"].shares.$post(
          { param, json: to },
          { headers: { [CSRF_HEADER]: csrfToken } },
        ),
      ),
    onSuccess: (result) => {
      setOutcome(result);
      setChosen(new Set());
      setEmails("");
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: conversationKeys.lists });
      return queryClient.invalidateQueries({ queryKey: sharesKey });
    },
  });
  const unshare = useMutation({
    mutationFn: async (recipient: string) => {
      const res = await api.api.conversations[":conversationId"].shares[":userId"].$delete(
        { param: { ...param, userId: recipient } },
        { headers: { [CSRF_HEADER]: csrfToken } },
      );
      if (!res.ok) {
        throw await apiError(res);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: conversationKeys.lists });
      return queryClient.invalidateQueries({ queryKey: sharesKey });
    },
  });

  const sharedWith = new Set(shares.data?.map((s) => s.userId) ?? []);
  // Everyone else who can read the project; candidates are those not shared with yet.
  const others = (pool ?? []).filter((m) => m.userId !== userId);
  const candidates = others.filter((m) => !sharedWith.has(m.userId));
  // Nobody else can read the project: never claimed while someone is shared with already, nor
  // while a search narrows the list.
  const alone = others.length === 0 && sharedWith.size === 0 && q === "";
  const searchable = people.data?.truncated === true || q !== "";
  const error = share.error ?? unshare.error ?? shares.error;

  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={(e) => {
        e.preventDefault();
        if (share.isPending) {
          return;
        }
        if (standalone ? typed.length > 0 : chosen.size > 0) {
          setOutcome(null);
          share.mutate(standalone ? { emails: typed } : { userIds: [...chosen] });
        }
      }}
    >
      <section aria-labelledby={`${id}-current`} className="flex flex-col gap-2">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 id={`${id}-current`} className="text-sm font-medium text-fg">
            {t("share.sharedWith")}
          </h3>
          {shares.data ? (
            <AudienceLine
              restricted={shares.data.length === 0 && props.projectShare == null}
              text={conversationAudience({
                owned: true,
                shares: shares.data.length,
                project: props.projectShare != null,
              })}
              webResults={webResults}
              ranCode={props.ranCode === true}
            />
          ) : webResults ? (
            <WebResultsNote />
          ) : null}
        </div>
        {shares.data && shares.data.length > 0 ? (
          <ul className="divide-y divide-line rounded-lg border border-line bg-surface-raised text-sm">
            {shares.data.map((s) => (
              <li key={s.userId} className="flex items-center justify-between gap-3 px-3 py-1.5">
                <span className="text-fg">{nameOf(s.userId)}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  disabled={unshare.isPending || frozen}
                  onClick={() => unshare.mutate(s.userId)}
                >
                  {t("share.stop")}
                  <span className="sr-only"> {nameOf(s.userId)}</span>
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-fg-secondary">
            {shares.isPending ? t("app.loading") : t("share.nobody")}
          </p>
        )}
      </section>

      {offersProject ? (
        <ProjectShareSection conversationId={conversationId} frozen={frozen} />
      ) : null}

      {standalone ? (
        // No directory is shown: people are named by their email address, checked on send.
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-emails`}>{t("share.emails")}</Label>
          <Textarea
            id={`${id}-emails`}
            value={emails}
            rows={2}
            disabled={share.isPending}
            placeholder={t("share.emailsPlaceholder")}
            aria-describedby={`${id}-emails-hint`}
            onChange={(e) => setEmails(e.currentTarget.value)}
          />
          <p id={`${id}-emails-hint`} className="text-xs text-fg-muted">
            {t("share.emailsHint")}
          </p>
        </div>
      ) : (
        <fieldset className="flex flex-col gap-2" disabled={share.isPending || frozen}>
          <legend className="mb-2 text-sm font-medium text-fg">{t("share.add")}</legend>
          {searchable ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${id}-find`} className="sr-only">
                {t("share.find")}
              </Label>
              <Input
                id={`${id}-find`}
                type="search"
                value={find}
                placeholder={t("share.find")}
                aria-describedby={people.data?.truncated ? `${id}-find-hint` : undefined}
                onChange={(e) => setFind(e.currentTarget.value)}
              />
              {people.data?.truncated ? (
                <p id={`${id}-find-hint`} className="text-xs text-fg-muted">
                  {t("share.findHint")}
                </p>
              ) : null}
            </div>
          ) : null}
          {pool === undefined ? (
            <p className="text-sm text-fg-secondary">
              {people.isPending ? t("app.loading") : t("share.membersUnavailable")}
            </p>
          ) : alone ? (
            // No one else to share with: never worded as if it were shared already.
            <div className="flex flex-col gap-1 text-sm">
              <p className="text-fg-secondary">{t("share.onlyYou")}</p>
              {canManageMembers && projectId ? (
                <p>
                  <Link
                    href={projectSettingsPath(projectId, "members")}
                    onClick={onClose}
                    className="text-accent-text underline-offset-4 hover:underline"
                  >
                    {t("share.addPeople")}
                  </Link>
                </p>
              ) : (
                <p className="text-fg-muted">{t("share.addPeopleHint")}</p>
              )}
            </div>
          ) : candidates.length === 0 ? (
            <p className="text-sm text-fg-secondary">
              {q === "" ? t("share.noCandidates") : t("share.noMatch")}
            </p>
          ) : (
            <ul className="flex max-h-56 flex-col gap-2 overflow-y-auto">
              {candidates.map((m) => (
                <li key={m.userId} className="flex items-center gap-2.5">
                  <Checkbox
                    id={`${id}-member-${m.userId}`}
                    checked={chosen.has(m.userId)}
                    onCheckedChange={(on) => {
                      const next = new Set(chosen);
                      if (on === true) {
                        next.add(m.userId);
                      } else {
                        next.delete(m.userId);
                      }
                      setChosen(next);
                    }}
                  />
                  <Label htmlFor={`${id}-member-${m.userId}`} className="font-normal">
                    {m.displayName}
                    <span className="text-fg-muted">{t(`projectRoles.${m.role}.name`)}</span>
                  </Label>
                </li>
              ))}
            </ul>
          )}
        </fieldset>
      )}
      {outcome ? (
        <div className="flex flex-col gap-2" aria-live="polite">
          {outcome.shared.length > 0 ? (
            <Notice tone="success">
              {t("share.sharedNow", { names: formatList(outcome.shared.map(nameOf)) })}
            </Notice>
          ) : null}
          {outcome.refused.length > 0 ? (
            <div role="alert">
              <Notice tone="warning" title={t("share.refused", { count: outcome.refused.length })}>
                <ul className="mt-1 list-disc pl-5">
                  {outcome.refused.map((r) => (
                    // Use the local member name and catalogue reason; never server wording.
                    <li key={r.userId}>
                      {r.code === "cannot_receive"
                        ? t("share.cannotReceive", { email: r.userId })
                        : t("share.refusedPerson", {
                            name: nameOf(r.userId),
                            reason: errorCopy(r.code) ?? t("errors.refused"),
                          })}
                    </li>
                  ))}
                </ul>
              </Notice>
            </div>
          ) : null}
        </div>
      ) : null}
      {error ? <Notice tone="danger">{error.message}</Notice> : null}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {t("share.done")}
        </Button>
        <Button
          type="submit"
          disabled={
            (standalone ? typed.length === 0 : chosen.size === 0) || share.isPending || frozen
          }
        >
          {share.isPending ? t("share.sharing") : t("share.submit")}
        </Button>
      </DialogFooter>
    </form>
  );
}
